import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useLoad } from "@/lib/hooks";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section, NativeSelect, StatusBadge } from "@/components/common";

const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const monday = () => { const d = new Date(); const w = (d.getDay() + 6) % 7; d.setDate(d.getDate() - w); return d.toISOString().slice(0, 10); };
const tone = (pct) => (pct > 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-400" : "bg-emerald-500");
const SCHEDULERS = ["tenant_admin", "production"];
const mondayOf = (iso) => { const d = new Date(`${iso}T00:00:00`); return addDays(iso, -((d.getDay() + 6) % 7)); };
const dayLabel = (iso, opts) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, opts);

function useIsMobile() {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => { const mq = window.matchMedia(q); const f = (e) => setM(e.matches); mq.addEventListener("change", f); return () => mq.removeEventListener("change", f); }, []);
  return m;
}

function DaySwitcher({ week, sel, go }) {
  const ref = useRef(null);
  const move = (n) => { const day = addDays(sel, n); go(day); requestAnimationFrame(() => ref.current?.querySelector(`[data-day="${day}"]`)?.focus()); };
  const onKey = (e) => { if (e.key === "ArrowLeft" || e.key === "ArrowRight") { e.preventDefault(); move(e.key === "ArrowLeft" ? -1 : 1); } };
  const days = [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(week, i));
  return (
    <div className="mb-3 space-y-2" data-testid="cap-day-switcher" onKeyDown={onKey} ref={ref}>
      <div className="flex items-center justify-between gap-2">
        <Button size="icon" variant="outline" aria-label="Previous day" data-testid="cap-prev-day" onClick={() => go(addDays(sel, -1))}><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-sm font-semibold" aria-live="polite" data-testid="cap-day-label">{dayLabel(sel, { weekday: "long", day: "numeric", month: "short", year: "numeric" })}</span>
        <Button size="icon" variant="outline" aria-label="Next day" data-testid="cap-next-day" onClick={() => go(addDays(sel, 1))}><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <div role="tablist" aria-label="Days of the week" className="grid grid-cols-7 gap-1">{days.map((x) => (
        <button key={x} type="button" role="tab" data-day={x} aria-selected={x === sel} tabIndex={x === sel ? 0 : -1} data-testid={`cap-day-chip-${x}`} onClick={() => go(x)}
          className={`rounded-md px-1 py-1.5 text-center text-[11px] leading-tight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#45BFB4] ${x === sel ? "bg-[#13294B] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          <div className="font-medium">{dayLabel(x, { weekday: "short" })}</div><div>{dayLabel(x, { day: "numeric" })}</div></button>))}</div>
    </div>
  );
}

function applyMove(d, b, wc, day) {
  const moved = d.blocks.map((x) => (x.job_id !== b.job_id ? x : { ...x, day, work_center: x.work_center === b.work_center ? wc : x.work_center }));
  const merged = [];
  moved.forEach((x) => {
    const m = merged.find((y) => y.job_id === x.job_id && y.work_center === x.work_center);
    if (m) m.hours = Math.round((m.hours + x.hours) * 100) / 100; else merged.push({ ...x });
  });
  const used = {};
  merged.forEach((x) => { const k = `${x.work_center}|${x.day}`; used[k] = Math.round(((used[k] || 0) + x.hours) * 100) / 100; });
  return { ...d, blocks: merged, used };
}

function Cell({ wc, day, blocks, used, onPick, canDrag, onDrop }) {
  const [over, setOver] = useState(false);
  const cap = wc.daily_capacity_hours || 8;
  const pct = Math.round(((used || 0) / cap) * 100);
  const drop = (e) => { e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData("text/plain"), wc, day); };
  return (
    <td className={`min-w-[130px] align-top transition-colors ${over ? "bg-[#45BFB4]/15" : ""}`} data-testid={`cap-cell-${wc.code}-${day}`}
      onDragOver={canDrag ? (e) => { e.preventDefault(); setOver(true); } : undefined} onDragLeave={() => setOver(false)} onDrop={canDrag ? drop : undefined}>
      <div className="mb-1 h-1.5 w-full rounded bg-slate-100" title={`${used || 0} of ${cap} h`}><div className={`h-1.5 rounded ${tone(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} data-testid={`cap-bar-${wc.code}-${day}`} /></div>
      <div className="mb-1 text-[10px] text-slate-400" data-testid={`cap-used-${wc.code}-${day}`}>{used || 0}/{cap} h</div>
      <div className="space-y-1">{blocks.map((b) => (
        <button key={b.job_id} type="button" onClick={() => onPick(b)} data-testid={`cap-block-${b.number}-${wc.code}`} draggable={canDrag}
          onDragStart={(e) => e.dataTransfer.setData("text/plain", `${b.job_id}|${b.work_center}`)}
          className={`block w-full truncate rounded px-1.5 text-left text-[11px] text-white ${canDrag ? "cursor-grab active:cursor-grabbing" : ""} ${b.rush || b.priority === "urgent" ? "bg-rose-600" : "bg-[#13294B]"}`} style={{ minHeight: `${Math.max(18, (b.hours / cap) * 96)}px` }}>
          {b.number} · {b.hours}h</button>))}</div>
    </td>
  );
}

function JobPanel({ pick, onClose }) {
  return (
    <Sheet open={!!pick} onOpenChange={(o) => !o && onClose()}><SheetContent data-testid="cap-job-panel">
      {pick && <><SheetHeader><SheetTitle>{pick.number} · {pick.title}</SheetTitle><SheetDescription>{pick.customer_name}</SheetDescription></SheetHeader>
        <div className="mt-4 space-y-2 text-sm"><StatusBadge status={pick.status} /><div>Due {pick.day} · {pick.hours} h on {pick.work_center}</div><div>Priority: <b className="capitalize">{pick.priority || "normal"}</b>{pick.rush && " · Rush job"}</div>
          <Link to={`/app/jobs/${pick.job_id}`} className="inline-block text-[#13294B] underline" data-testid="cap-open-job">Open job detail</Link></div></>}
    </SheetContent></Sheet>
  );
}

export default function Capacity() {
  const [week, setWeek] = useState(monday());
  const [branch, setBranch] = useState("");
  const [lk] = useLoad("/tenant/branches");
  const branches = Array.isArray(lk) ? lk : lk?.branches || [];
  const [loaded] = useLoad(`/jobs-capacity?week_start=${week}${branch ? `&branch_id=${branch}` : ""}`);
  const [d, setD] = useState(null);
  useEffect(() => setD(loaded), [loaded]);
  const [pick, setPick] = useState(null);
  const { user } = useAuth();
  const canDrag = SCHEDULERS.includes(user?.role) && (user?.tenant?.access_mode || "full") === "full";
  const mobile = useIsMobile();
  const [sel, setSel] = useState(new Date().toISOString().slice(0, 10));
  useEffect(() => { if (mondayOf(sel) !== week) setSel(week); }, [week]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = (day) => { setSel(day); if (mondayOf(day) !== week) setWeek(mondayOf(day)); };
  const shown = d && (mobile ? [sel].filter((x) => d.days.includes(x)) : d.days);

  const onDrop = async (key, wc, day) => {
    const [jobId, fromWc] = (key || "").split("|");
    const b = d.blocks.find((x) => x.job_id === jobId && x.work_center === fromWc);
    if (!b || (b.day === day && b.work_center === wc.code)) return;
    const prev = d;
    const next = applyMove(d, b, wc.code, day);
    setD(next);
    try {
      await api.patch(`/jobs/${jobId}`, { due_date: day, ...(wc.code !== fromWc ? { work_center_from: fromWc, work_center_to: wc.code } : {}) });
      toast.success(`${b.number} rescheduled to ${wc.name} · ${day}`);
      if ((next.used[`${wc.code}|${day}`] || 0) > (wc.daily_capacity_hours || 8)) toast.warning(`${wc.name} is over capacity on ${day}`);
    } catch (e) {
      setD(prev);
      toast.error(errMsg(e));
    }
  };

  return (
    <>
      <PageHeader title="Capacity planner" subtitle={canDrag ? "Drag job blocks between days and work centres to reschedule." : "Jobs due this week by work centre, sized by remaining estimated machine hours."} />
      {!mobile && <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button size="icon" variant="outline" data-testid="cap-prev-week" onClick={() => setWeek(addDays(week, -7))}><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-sm font-medium" data-testid="cap-week-label">Week of {week}</span>
        <Button size="icon" variant="outline" data-testid="cap-next-week" onClick={() => setWeek(addDays(week, 7))}><ChevronRight className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" data-testid="cap-this-week" onClick={() => setWeek(monday())}>This week</Button>
        <div className="w-56"><NativeSelect testId="cap-branch" value={branch} onChange={setBranch} options={[{ value: "", label: "All branches" }, ...branches.map((x) => ({ value: x.id, label: x.name }))]} /></div>
        {!canDrag && <span className="text-xs text-slate-400" data-testid="cap-readonly-note">Read-only view</span>}
      </div>}
      {mobile && <><DaySwitcher week={week} sel={sel} go={go} />
        <div className="mb-3 flex items-center gap-2"><div className="flex-1"><NativeSelect testId="cap-branch" value={branch} onChange={setBranch} options={[{ value: "", label: "All branches" }, ...branches.map((x) => ({ value: x.id, label: x.name }))]} /></div>
          {!canDrag && <span className="text-xs text-slate-400" data-testid="cap-readonly-note">Read-only view</span>}</div></>}
      <Section testId="capacity-grid">{d && <div className="overflow-x-auto"><table className="po-table">
        <thead><tr><th>Work centre</th>{shown.map((x) => <th key={x}>{new Date(`${x}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</th>)}</tr></thead>
        <tbody>{d.work_centers.map((wc) => <tr key={wc.code} data-testid={`cap-row-${wc.code}`}><td className="font-medium">{wc.name}<div className="text-xs text-slate-400">{wc.daily_capacity_hours} h/day</div></td>
          {shown.map((day) => <Cell key={day} wc={wc} day={day} used={d.used[`${wc.code}|${day}`]} blocks={d.blocks.filter((x) => x.work_center === wc.code && x.day === day)} onPick={setPick} canDrag={canDrag} onDrop={onDrop} />)}</tr>)}</tbody>
      </table></div>}{d && (mobile ? !d.blocks.some((x) => x.day === sel) : d.blocks.length === 0) && <p className="px-5 py-4 text-sm text-slate-400" data-testid="cap-empty">{mobile ? "No scheduled or in-progress jobs due on this day." : "No scheduled or in-progress jobs with a due date this week."}</p>}</Section>
      <JobPanel pick={pick} onClose={() => setPick(null)} />
    </>
  );
}
